import { ReactNode } from 'react';

import {
  MetricsBox,
  Pie,
  PieChartContainer,
  Skeleton,
  Typography,
  Box,
  Stack,
  tokens,
} from '@cast/design-system';

import { FleetMetricRow, MetricTileCard } from './FleetMetricTile';
import { formatFleetMetricNumber } from '../utils/fleetMetricFormat';
import { getResourceMetricChartData } from '../utils/resourceMetricChartData';

const PIE_CHART_RADIUS = 34;
const PIE_CHART_SIZE = PIE_CHART_RADIUS * 2;

const RESOURCE_METRIC_CHART_COLORS = {
  used: tokens.theme.data.green500,
  remainingConnected: tokens.theme.data.grey200,
  discovered: tokens.theme.data.grey400,
};

type ResourceMetricTileProps = {
  connected: number;
  discovered: number;
  discoveredUnit?: ReactNode;
  formatDiscovered?: (value: number) => ReactNode;
  formatTotal: (value: number) => ReactNode;
  isLoading?: boolean;
  testId?: string;
  title: string;
  unit: string;
  used?: number;
};

export const ResourceMetricTile = ({
  connected,
  discovered,
  discoveredUnit,
  formatDiscovered,
  formatTotal,
  isLoading = false,
  testId,
  title,
  unit,
  used = 0,
}: ResourceMetricTileProps) => {
  const total = connected + discovered;
  const hasData = total > 0;
  const showDiscovered = discovered > 0;
  const formatDiscoveredValue =
    formatDiscovered ?? ((value: number) => formatFleetMetricNumber(value));
  const chartData = getResourceMetricChartData({
    connected,
    discovered,
    used,
    colors: RESOURCE_METRIC_CHART_COLORS,
  });

  return (
    <MetricTileCard>
      <Stack gap={12} height="100%" data-testid={testId}>
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          gap={12}
        >
          <Box minWidth={0}>
            {isLoading ? (
              <Stack gap="4px">
                <Skeleton variant="text" width="50%" height={16} />
                <Skeleton variant="rounded" width={96} height={28} />
              </Stack>
            ) : (
              <MetricsBox
                size="l"
                testId={`${testId}-total`}
                slots={{
                  label: title,
                  value: formatTotal(total),
                  unit,
                }}
                slotProps={{
                  label: {
                    color: tokens.theme.fg.structural.secondary,
                    sx: { textTransform: 'uppercase' },
                  },
                }}
              />
            )}
          </Box>

          <Box flexShrink={0} width={PIE_CHART_SIZE} height={PIE_CHART_SIZE}>
            {isLoading ? (
              <Skeleton
                variant="circular"
                width={PIE_CHART_SIZE}
                height={PIE_CHART_SIZE}
              />
            ) : hasData ? (
              <PieChartContainer data={chartData} radius={PIE_CHART_RADIUS}>
                <Pie dataKey="value" />
              </PieChartContainer>
            ) : null}
          </Box>
        </Stack>

        <Stack gap={2} sx={{ mt: 'auto' }}>
          {isLoading ? (
            <Skeleton variant="text" width="100%" height={17} />
          ) : showDiscovered ? (
            <Box data-testid={`${testId}-discovered`}>
              <FleetMetricRow
                label="Discovered"
                markerColor={RESOURCE_METRIC_CHART_COLORS.discovered}
                value={formatDiscoveredValue(discovered)}
                unit={discoveredUnit}
              />
            </Box>
          ) : hasData ? null : (
            <Typography
              variant="P10R"
              color={tokens.theme.fg.structural.secondary}
              fontStyle="italic"
            >
              No data yet
            </Typography>
          )}
        </Stack>
      </Stack>
    </MetricTileCard>
  );
};
